// Copyright (c) 2026 Celadon Development LLC, All rights reserved.
// Author Vasily Klyuev <vasily.klyuev@celadon.ae>
// Keeps the cached "is this account a bookshop" flag honest.
//
// The flag is written once, at login, from the login response - so an account
// promoted to a bookshop in COMS mid-session carried on seeing the ordinary
// customer interface until it logged out and back in. Nothing was broken; the
// browser simply had no reason to ask again.
//
// The mobile app solves this by re-reading the customer whenever the app comes
// back to the foreground. This is the same idea for the browser: re-read on
// page load and whenever the tab is looked at again, which is exactly when an
// admin who has just flipped the switch in another tab comes back.
const KEY = 'isBookshop';

/**
 * Fired on window when the flag actually changes value - not on every check.
 * Anything rendering bookshop-only UI can listen and re-render.
 */
export const BOOKSHOP_STATUS_CHANGE = 'sib:bookshop-status-change';

/** The cached flag. Stored as 1/0 by the login saga. */
export const isBookshopAccount = () => !!parseInt(localStorage.getItem(KEY), 10);

/**
 * Re-reads the flag from the server and updates the cached copy.
 *
 * GetCustomerByID already returns IsBookshop, so this needs no new endpoint -
 * it is the same call the My profile page makes.
 *
 * Deliberately uses fetch rather than the shared api() helper. api() treats a
 * "Not Authorized." body as a session ending: it clears localStorage and
 * pushes the user to the login screen. That is right for a call the user
 * asked for, and wrong for a silent background check - an expired token would
 * throw somebody out of the page they were reading, on nothing but a tab
 * switch. This check is an enhancement, so it must never be the thing that
 * ends a session; a stale flag simply survives until the user's next real
 * request, which is how it behaved before.
 *
 * Resolves to the new value, or null when there is nobody signed in or the
 * request did not come back cleanly. A failure changes nothing: a flaky
 * connection must not strip a shop of its tabs mid-session.
 */
export const refreshBookshopStatus = async () => {
  const id = localStorage.getItem('loggedID');
  const token = localStorage.getItem('token');

  if (!id || !token) {
    return null;
  }

  let body = null;

  try {
    const response = await fetch(
      `/Sellitback.svc/GetCustomerByID?CustomerId=${id}&Token=${encodeURIComponent(token)}`,
      { headers: { Accept: 'application/json' }, method: 'GET' }
    );

    if (!response.ok) {
      return null;
    }

    body = await response.json();
  } catch (error) {
    return null;
  }

  // Absent rather than false: a payload without the field at all is a shape
  // this code does not understand, and guessing "not a bookshop" would strip
  // a working shop of its tabs.
  if (!body || typeof body.IsBookshop !== 'boolean') {
    return null;
  }

  const next = body.IsBookshop ? 1 : 0;
  const changed = parseInt(localStorage.getItem(KEY), 10) !== next;

  localStorage.setItem(KEY, next);

  if (changed) {
    window.dispatchEvent(new Event(BOOKSHOP_STATUS_CHANGE));
  }

  return next;
};
